// Global styles
import './assets/css/main.css'

import { buildApiHeaders, resolveInternalApiUrl } from '@/utils/apiHeaders'

import App from './App.vue'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
// Database initialization
import { initializeDatabase } from './database'
import router from './router'
import { setupSentry } from './utils/sentry'
import { installTelemetryListeners } from './telemetry/telemetryListeners'

// Using MongoDB database with abstraction layer
const DEFAULT_API_BASE_URL = 'https://staging.services.leadconnectorhq.com/hltestmanager'

async function checkApiHealthOnStartup(): Promise<void> {
  try {
    const configuredApiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? DEFAULT_API_BASE_URL
    const healthUrl = resolveInternalApiUrl('/health', configuredApiBaseUrl)
    const response = await fetch(healthUrl, {
      headers: buildApiHeaders({
        url: healthUrl,
        includeAuth: false,
      }),
    })

    if (!response.ok) {
      throw new Error('API server not responding')
    }
  } catch {
    throw new Error('API server not available. Make sure to start the API server.')
  }
}

// Initialize database before creating the app
async function initializeApp(): Promise<void> {
  try {
    await checkApiHealthOnStartup()

    // Initialize database service
    await initializeDatabase()

    // Create Vue app
    const app = createApp(App)

    // Initialize Sentry
    setupSentry(app)

    // Use plugins
    app.use(createPinia())
    app.use(router)

    // Mount app
    app.mount('#app')
  } catch (error) {
    console.error('Failed to initialize application:', error)

    // In development mode with auth skip, continue anyway
    if (import.meta.env.VITE_SKIP_AUTH === 'true') {
      console.warn('🚀 DEVELOPMENT MODE: Database initialization failed, but continuing anyway...')

      // Keep services wired even when startup health fails in dev mode.
      try {
        await initializeDatabase()
      } catch (dbError) {
        console.warn('⚠️ DEVELOPMENT MODE: Database service initialization also failed:', dbError)
      }

      // Create Vue app even with database error
      const app = createApp(App)

      // Initialize Sentry
      setupSentry(app)

      // Use plugins
      app.use(createPinia())
      app.use(router)

      // Mount app with warning banner
      app.mount('#app')

      // Note: Warning notification will be shown via useToast composable if needed

      return
    }

    // Show error to user
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    document.body.innerHTML = `
      <div style="display: flex; justify-content: center; align-items: center; height: 100vh; flex-direction: column; font-family: Arial, sans-serif;">
        <h1 style="color: red;">Application Initialization Failed</h1>
        <p>Please check your database configuration and try again.</p>
        <p style="color: #666; font-size: 0.9em;">${errorMessage}</p>
        <button onclick="window.location.reload()" style="margin-top: 20px; padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">Retry</button>
      </div>
    `
  }
}

// Initialize the application
installTelemetryListeners()
initializeApp()
